import "./DealRequest.scss";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Row, Col, Typography, Upload, App, Alert, Steps } from "antd";
import CustomInput from "../../components/CustomInput/Input";
import {
  projectDurationRules,
  handleUploadFile,
  expectedRoiJvRules,
  profitShareJvRules,
  fundReqJvRules,
  projectNameJvRules,
  projectDescriptionJvRules,
  investmentStructureJvRules,
  profitSharingModelJvRules,
  riskFactorsJvRules,
} from "../../helpers/helpers";
import UploadIcon from "../../assets/icons/document-upload.svg";
import CustomButton from "../../components/CustomButton/Button";
import CustomInputNumber from "../../components/CustomInputNumber/InputNumber";
import PrimaryButton from "../../components/CustomButton/PrimaryButton";
import { useSelector, useDispatch } from "react-redux";
import { dealRequest, dealAutofill, resetDealAutofill } from "../../redux/slices/DealReqSlice";
import { SUCCESS_MSGS } from "../../constants/constants";

const { Dragger } = Upload;

const { Text, Title } = Typography;

export const toAutofillNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const stripped = String(value).replace(/[^0-9.-]/g, "");
  if (!stripped || stripped === "-" || stripped === ".") return null;
  const parsed = Number(stripped);
  return Number.isFinite(parsed) ? parsed : null;
};

export const buildUploadedFile = (url, uid = "-1") => {
  if (!url) return null;
  const rawName = String(url).split("?")[0].split("/").pop() || "document.pdf";
  const name = decodeURIComponent(rawName);

  return {
    uid: String(uid),
    name,
    status: "done",
    percent: 100,
    url,
    response: { url, fileUrl: url },
  };
};

export const JV_AUTOFILL_FIELD_KEYS = [
  "projectName",
  "projectDescription",
  "projectDuration",
  "projectValue",
  "fundRequired",
  "expectedRoi",
  "roi",
  "profitShare",
  "investmentStructure",
  "profitSharingModel",
  "profitSharing",
  "riskFactors",
  "projectProposal",
  "pitchDeck",
  "financialProjections",
  "finProj",
  "supportingDocs",
  "amountToRaise",
  "dealDetails",
  "otherTermsConditions",
];

export const hasJvDealAutofillPayload = (payload) => {
  if (!payload || typeof payload !== "object") return false;
  return JV_AUTOFILL_FIELD_KEYS.some(
    (key) => payload[key] != null && payload[key] !== ""
  );
};

export const applyAutofillToForm = (autofillData, form, setters) => {
  if (!autofillData) return;

  const { setDocData, setFileList } = setters;
  const formValues = {};

  [
    "projectName",
    "projectDescription",
    "investmentStructure",
    "riskFactors",
  ].forEach((field) => {
    const value =
      autofillData[field] ??
      (field === "projectDescription" ? autofillData.dealDetails : undefined) ??
      (field === "riskFactors" ? autofillData.otherTermsConditions : undefined);
    if (value != null && value !== "") {
      formValues[field] = value;
    }
  });

  const profitSharingValue =
    autofillData.profitSharing ?? autofillData.profitSharingModel;
  if (profitSharingValue != null && profitSharingValue !== "") {
    formValues.profitSharing = profitSharingValue;
  }

  if (autofillData.projectDuration != null && autofillData.projectDuration !== "") {
    formValues.projectDuration = String(autofillData.projectDuration);
  }

  ["projectValue", "fundRequired", "profitShare"].forEach((field) => {
    const value =
      autofillData[field] ??
      (field === "fundRequired" ? autofillData.amountToRaise : undefined);
    const numeric = toAutofillNumber(value);
    if (numeric != null) {
      formValues[field] = numeric;
    }
  });

  const roiValue = autofillData.roi ?? autofillData.expectedRoi;
  const roiNumeric = toAutofillNumber(roiValue);
  if (roiNumeric != null) {
    formValues.roi = roiNumeric;
  }

  form.setFieldsValue(formValues);

  const proposalUrl = autofillData.projectProposal || autofillData.pitchDeck;
  if (proposalUrl) {
    const proposalFile = buildUploadedFile(proposalUrl, "autofill-proposal");
    if (proposalFile) {
      setDocData((prev) => ({ ...prev, projectProposal: proposalUrl }));
      setFileList((prev) => ({ ...prev, projectProposal: [proposalFile] }));
      form.setFieldsValue({ projectProposal: [proposalFile] });
    }
  }

  const finProjUrl =
    autofillData.financialProjections || autofillData.finProj;
  if (finProjUrl) {
    const finProjFile = buildUploadedFile(finProjUrl, "autofill-finproj");
    if (finProjFile) {
      setDocData((prev) => ({ ...prev, finProj: finProjUrl }));
      setFileList((prev) => ({ ...prev, finProj: [finProjFile] }));
      form.setFieldsValue({ finProj: [finProjFile] });
    }
  }

  const supportingDocsUrl = autofillData.supportingDocs;
  if (supportingDocsUrl) {
    const supportingDocsFile = buildUploadedFile(
      supportingDocsUrl,
      "autofill-supporting"
    );
    if (supportingDocsFile) {
      setDocData((prev) => ({ ...prev, supportingDocs: supportingDocsUrl }));
      setFileList((prev) => ({ ...prev, supportingDocs: [supportingDocsFile] }));
      form.setFieldsValue({ supportingDocs: [supportingDocsFile] });
    }
  }
};

const DealRequest = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state?.auth);
  const { dealReqLoading, dealReqError } = useSelector(
    (state) => state?.dealReq || {}
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [showUploadErrors, setShowUploadErrors] = useState(false);

  const [docData, setDocData] = useState({
    projectProposal: null,
    finProj: null,
    supportingDocs: null,
  });

  const [fileList, setFileList] = useState({
    projectProposal: [],
    finProj: [],
    supportingDocs: [],
  });

  useEffect(() => {
    form.resetFields();
    setDocData({
      projectProposal: null,
      finProj: null,
      supportingDocs: null,
    });
    setFileList({
      projectProposal: [],
      finProj: [],
      supportingDocs: [],
    });

    if (!user?.orgId) {
      if (typeof resetDealAutofill === "function") {
        dispatch(resetDealAutofill());
      }
      return undefined;
    }

    let cancelled = false;
    if (typeof dealAutofill === "function") {
      const thunkPromise = dispatch(dealAutofill({ orgId: user.orgId }));
      if (thunkPromise && typeof thunkPromise.then === "function") {
        thunkPromise.then((result) => {
          if (cancelled || result?.meta?.requestStatus !== "fulfilled") return;
          if (!hasJvDealAutofillPayload(result.payload)) return;
          applyAutofillToForm(result.payload, form, {
            setDocData,
            setFileList,
          });
        });
      }
    }

    return () => {
      cancelled = true;
    };
  }, [dispatch, form, user?.orgId]);

  const steps = [
    {
      title: "Basic Deal Information",
    },
    {
      title: "Funding & Returns",
    },
    {
      title: "Additional Information",
    },
    {
      title: "Document Uploads",
    },
  ];

  const stepFields = [
    [
      "projectName",
      "projectDescription",
      "projectDuration",
    ],
    [
      "projectValue",
      "fundRequired",
      "roi",
      "profitShare",
    ],
    [
      "investmentStructure",
      "profitSharing",
      "riskFactors",
    ],
    [],
  ];

  const next = async () => {
    try {
      const fieldsToValidate = stepFields[currentStep];

      if (fieldsToValidate?.length) {
        await form.validateFields(fieldsToValidate);
      }

      setShowUploadErrors(false);
      setCurrentStep((prev) => prev + 1);
    } catch {
      // validation failed
    }
  };

  const prev = () => {
    setShowUploadErrors(false);
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setShowUploadErrors(true);

    if (!docData.projectProposal || !docData.finProj) {
      return;
    }

    let values;

    try {
      values = await form.validateFields();
    } catch (error) {
      return;
    }

    const payload = {
      projectName: values.projectName,
      projectDescription: values.projectDescription,
      projectDuration: values.projectDuration,
      projectValue: values.projectValue,
      fundRequired: values.fundRequired,
      expectedRoi: values.roi,
      profitShare: values.profitShare,
      investmentStructure: values.investmentStructure,
      profitSharingModel: values.profitSharing,
      riskFactors: values.riskFactors,
      projectProposal: docData.projectProposal,
      financialProjections: docData.finProj,
      supportingDocs: docData.supportingDocs,
      orgId: user?.orgId,
    };

    const res = await dispatch(dealRequest(payload));
    if (res.payload.statusCode === 201) {
      message.success(SUCCESS_MSGS.DEAL_REQUEST_SUCCESS);

      form.resetFields();
      setCurrentStep(0);
      setShowUploadErrors(false);

      setDocData({
        projectProposal: null,
        finProj: null,
        supportingDocs: null,
      });
      setFileList({
        projectProposal: [],
        finProj: [],
        supportingDocs: [],
      });
      if (typeof resetDealAutofill === "function") {
        dispatch(resetDealAutofill());
      }
      navigate("/deal-management");
    }
  };

  const handleBeforeUpload = (type, file) => {
    const typeMap = {
      projectProposal: [
        "application/pdf",
      ],
      finProj: [
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ],
      supportingDocs: [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ],
    };

    const maxSizeMB = 20;

    const allowedTypes = typeMap[type];

    if (!allowedTypes.includes(file.type)) {
      message.error("Invalid file type selected");
      return Upload.LIST_IGNORE;
    }

    if (file.size / 1024 / 1024 > maxSizeMB) {
      message.error(`File must be smaller than ${maxSizeMB}MB`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleCustomRequest = async (type, options) => {
    const { file, onSuccess, onError, onProgress } = options;

    const cleanName = file.name.replace(/\s+/g, "_");
    const cleanFile = new File([file], cleanName, { type: file.type });

    try {
      const path = `business/${user?.orgId}`;
      const { fileUrl } = await handleUploadFile(cleanFile, path, onProgress);
      setDocData((prev) => ({
        ...prev,
        [type]: fileUrl,
      }));

      const uploadedFile = [
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          percent: 100,
          url: fileUrl,
        },
      ];

      setFileList((prev) => ({
        ...prev,
        [type]: uploadedFile,
      }));

      form.setFieldsValue({
        [type]: uploadedFile,
      });

      message.success(`File uploaded successfully`);
      onSuccess("ok");
    } catch (err) {
      onError(err);
    }
  };

  const projectProposalProps = {
    name: "file",
    multiple: false,
    accept: ".pdf",
    maxCount: 1,
    beforeUpload: (file) => handleBeforeUpload("projectProposal", file),
    customRequest: (options) => handleCustomRequest("projectProposal", options),
  };

  const finProjProps = {
    name: "file",
    multiple: false,
    accept: ".pdf,.xls,.xlsx",
    maxCount: 1,
    beforeUpload: (file) => handleBeforeUpload("finProj", file),
    customRequest: (options) => handleCustomRequest("finProj", options),
  };

  const supportingDocsProps = {
    name: "file",
    multiple: false,
    accept: ".pdf,.doc,.docx,.xls,.xlsx",
    maxCount: 1,
    beforeUpload: (file) => handleBeforeUpload("supportingDocs", file),
    customRequest: (options) => handleCustomRequest("supportingDocs", options),
  };

  return (
    <div className="deal-request-container">
      <Title level={3}>Deal Request</Title>

      <div className="stepper-card">
        {/* Dynamic Heading */}
        <div className="step-header">
          <Text className="step-heading">
            {steps[currentStep]?.title}
          </Text>
        </div>

        {/* Stepper */}
        <Steps
          current={currentStep}
          className="deal-stepper"
          progressDot
          labelPlacement="vertical"
          items={[
            { title: "1" },
            { title: "2" },
            { title: "3" },
            { title: "4" },
          ]}
        />

        <Form
          form={form}
          layout="vertical"
        >
          {/* STEP 1 */}
          <div style={{ display: currentStep === 0 ? "block" : "none" }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <CustomInput
                  label="Project Name"
                  name="projectName"
                  isRequired={true}
                  placeholder="Enter project name"
                  rules={projectNameJvRules}
                />
              </Col>
              <Col xs={24} md={12}>
                <CustomInput
                  label="Project Duration (Months)"
                  name="projectDuration"
                  isRequired={true}
                  placeholder="e.g., 24"
                  rules={projectDurationRules}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9]/g, "");
                  }}
                />
              </Col>

              {/* Project Description */}
              <Col xs={24}>
                <CustomInput
                  label="Project Description"
                  name="projectDescription"
                  type="textarea"
                  isRequired={true}
                  placeholder="Provide detailed project description"
                  rules={projectDescriptionJvRules}
                  maxLength={500}
                  showCount
                />
              </Col>
            </Row>
          </div>

          {/* STEP 2 */}
          <div style={{ display: currentStep === 1 ? "block" : "none" }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <CustomInputNumber
                  label="Project Value (₹)"
                  name="projectValue"
                  isRequired={true}
                  placeholder="e.g., 50000000"
                  controls={false}
                  rules={[
                    {
                      required: true,
                      message: "Project Value cannot be empty!",
                    },
                    {
                      validator: (_, value) => {
                        if (
                          value === undefined ||
                          value === null ||
                          value === ""
                        ) {
                          return Promise.resolve();
                        }

                        const num =
                          typeof value === "string"
                            ? Number(value.replace(/[^\d.]/g, ""))
                            : Number(value);

                        // Minimum validation
                        if (num < 200000) {
                          return Promise.reject(
                            new Error(
                              "Project Value must be at least ₹2,00,000"
                            )
                          );
                        }

                        // Maximum validation
                        if (num > 100000000000000) {
                          return Promise.reject(
                            new Error(
                              "Project Value cannot exceed ₹1,00,00,00,00,00,000"
                            )
                          );
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                  onChange={() => {
                    if (form.getFieldValue("fundRequired")) {
                      form.validateFields(["fundRequired"]).catch(() => {});
                    }
                  }}
                />
              </Col>

              <Col xs={24} md={12}>
                <CustomInputNumber
                  label="Fund Required (₹)"
                  name="fundRequired"
                  placeholder="e.g., 20000000"
                  controls={false}
                  isRequired={true}
                  dependencies={["projectValue"]}
                  rules={fundReqJvRules}
                />
              </Col>

              <Col xs={24} md={12}>
                <CustomInput
                  label="Expected ROI (%)"
                  name="roi"
                  placeholder="e.g., 18.5"
                  type="number"
                  isRequired={true}
                  min={0}
                  step={0.1}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                  }}
                  rules={expectedRoiJvRules}
                />
              </Col>

              <Col xs={24}>
                <CustomInput
                  label="Profit Share (%)"
                  name="profitShare"
                  placeholder="e.g., 18.5"
                  type="number"
                  isRequired={true}
                  min={0}
                  controls={false}
                  step={0.1}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                  }}
                  rules={profitShareJvRules}
                />
              </Col>
            </Row>
          </div>

          {/* STEP 3 */}
          <div style={{ display: currentStep === 2 ? "block" : "none" }}>
            <>
              <CustomInput
                label="Investment Structure"
                name="investmentStructure"
                isRequired={true}
                type="textarea"
                placeholder="Describe the investment structure (e.g., equity, debt, hybrid)"
                rules={investmentStructureJvRules}
                maxLength={500}
                showCount
              />

              <CustomInput
                label="Revenue / Profit Sharing Model"
                name="profitSharing"
                type="textarea"
                isRequired={true}
                placeholder="Describe the revenue or profit sharing model"
                rules={profitSharingModelJvRules}
                maxLength={500}
                showCount
              />

              <CustomInput
                label="Risk Factors (Optional)"
                name="riskFactors"
                type="textarea"
                placeholder="Describe any potential risks associated with this project"
                rules={riskFactorsJvRules}
                maxLength={500}
                showCount
              />
            </>
          </div>

          {/* STEP 4 */}
          <div style={{ display: currentStep === 3 ? "block" : "none" }}>
            <>
              <Form.Item
                label={
                  <span>
                    Upload Your Proposal
                    <span style={{ color: "red", marginLeft: 4 }}>*</span>
                  </span>
                }
                name="projectProposal"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
                validateStatus={showUploadErrors && !docData.projectProposal ? "error" : ""}
                help={showUploadErrors && !docData.projectProposal ? "Please Upload Your Proposal" : null}
              >
                <Dragger
                  {...projectProposalProps}
                  fileList={fileList.projectProposal}
                  onChange={({ fileList: newList }) => {
                    setFileList((prev) => ({
                      ...prev,
                      projectProposal: newList,
                    }));

                    form.setFieldsValue({
                      projectProposal: newList,
                    });
                  }}
                  onRemove={() => {
                    setDocData((prev) => ({
                      ...prev,
                      projectProposal: null,
                    }));

                    setFileList((prev) => ({
                      ...prev,
                      projectProposal: [],
                    }));

                    form.setFieldsValue({
                      projectProposal: [],
                    });
                  }}
                >
                  <img
                    src={UploadIcon}
                    alt="Upload"
                    style={{
                      width: 24,
                      height: 24,
                      marginBottom: 4,
                    }}
                  />
                  <p>Click or drag file to upload (.pdf)</p>
                </Dragger>
              </Form.Item>

              <Form.Item
                label={
                  <span>
                    Financial Projections
                    <span style={{ color: "red", marginLeft: 4 }}>*</span>
                  </span>
                }
                name="finProj"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
                validateStatus={showUploadErrors && !docData.finProj ? "error" : ""}
                help={showUploadErrors && !docData.finProj ? "Please upload financial projections" : null}
              >
                <Dragger
                  {...finProjProps}
                  fileList={fileList.finProj}
                  onChange={({ fileList: newList }) => {
                    setFileList((prev) => ({ ...prev, finProj: newList }));
                    form.setFieldsValue({ finProj: newList });
                  }}
                  onRemove={() => {
                    setDocData((prev) => ({ ...prev, finProj: null }));
                    setFileList((prev) => ({ ...prev, finProj: [] }));
                    form.setFieldsValue({ finProj: [] });
                  }}
                >
                  <img
                    src={UploadIcon}
                    alt="Upload"
                    style={{
                      width: 24,
                      height: 24,
                      marginBottom: 4,
                    }}
                  />
                  <p>Click or drag file to upload (.pdf, .csv)</p>
                </Dragger>
              </Form.Item>

              <Form.Item
                label="Supporting Documents (Optional)"
                name="supportingDocs"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
              >
                <Dragger
                  {...supportingDocsProps}
                  fileList={fileList.supportingDocs}
                  onChange={({ fileList: newList }) => {
                    setFileList((prev) => ({ ...prev, supportingDocs: newList }));
                    form.setFieldsValue({ supportingDocs: newList });
                  }}
                  onRemove={() => {
                    setDocData((prev) => ({ ...prev, supportingDocs: null }));
                    setFileList((prev) => ({ ...prev, supportingDocs: [] }));
                    form.setFieldsValue({ supportingDocs: [] });
                  }}
                >
                  <img
                    src={UploadIcon}
                    alt="Upload"
                    style={{
                      width: 24,
                      height: 24,
                      marginBottom: 4,
                    }}
                  />
                  <p>Click or drag file to upload (.pdf, .doc, .docx, .xls, .xlsx, .csv)</p>
                </Dragger>
              </Form.Item>
              {dealReqError && (
                <Alert
                  message={dealReqError}
                  type="error"
                  showIcon
                  style={{ marginBottom: 20 }}
                />
              )}
            </>
          </div>

          {/* Buttons */}
          <div className="step-buttons">
            {currentStep > 0 ? (
              <CustomButton
                htmlType="button"
                onClick={prev}
                style={{
                  padding: "24px",
                }}
              >
                Previous
              </CustomButton>
            ) : (
              <div />
            )}

            {currentStep < 3 ? (
              <PrimaryButton
                htmlType="button"
                onClick={next}
              >
                Next
              </PrimaryButton>
            ) : (
              <PrimaryButton
                htmlType="button"
                onClick={handleSubmit}
                loading={dealReqLoading}
              >
                Submit
              </PrimaryButton>
            )}
          </div>
        </Form>
      </div >
    </div >
  );
};

export default DealRequest;
